import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faLock, faStar } from "@fortawesome/free-solid-svg-icons";
import { classifyTicketReward, useTicketRewardContext } from "./rewardClient";
import "./Rewards.css";

const emptyFlags = {
    difficult: { enabled: false },
    urgent_management_request: { enabled: false },
};

const RewardClassificationPanel = ({ ticket, userId, onSaved }) => {
    const isCurrentAssignee = Boolean(ticket?.id && userId && ticket.assignee_id === userId);
    const { data: context, loading, error: loadError, refresh } = useTicketRewardContext(ticket?.id, isCurrentAssignee);
    const contextMatchesCurrentAssignment = !context?.currentAssigneeId
        || context.currentAssigneeId === userId;
    const flags = contextMatchesCurrentAssignment ? (context?.flags || emptyFlags) : emptyFlags;
    const locked = !ticket
        || ["completed", "cancelled"].includes(ticket.status)
        || (contextMatchesCurrentAssignment && context?.status && context.status !== "open");

    const baseDifficult = Boolean(flags?.difficult?.enabled);
    const baseUrgent = Boolean(flags?.urgent_management_request?.enabled);
    const [difficult, setDifficult] = useState(baseDifficult);
    const [urgent, setUrgent] = useState(baseUrgent);
    const [reason, setReason] = useState("");
    const [state, setState] = useState({ saving: false, error: "", success: "" });

    useEffect(() => {
        setDifficult(baseDifficult);
        setUrgent(baseUrgent);
        setReason("");
        setState({ saving: false, error: "", success: "" });
    }, [ticket?.id, context?.workUnitId, baseDifficult, baseUrgent]);

    const dirty = useMemo(
        () => difficult !== baseDifficult || urgent !== baseUrgent,
        [difficult, urgent, baseDifficult, baseUrgent],
    );

    if (!isCurrentAssignee || !ticket) return null;

    const save = async () => {
        if (!dirty || !reason.trim() || locked || state.saving) return;
        setState({ saving: true, error: "", success: "" });
        try {
            const result = await classifyTicketReward({
                ticketId: ticket.id,
                userId,
                difficult,
                urgentManagementRequest: urgent,
                reason: reason.trim(),
            });
            setDifficult(Boolean(result?.flags?.difficult?.enabled));
            setUrgent(Boolean(result?.flags?.urgent_management_request?.enabled));
            setReason("");
            setState({ saving: false, error: "", success: "Reward classification saved." });
            await refresh();
            onSaved?.();
        } catch (error) {
            setState({ saving: false, error: error.message || "Could not save reward classification.", success: "" });
        }
    };

    return (
        <section className="rw-classification">
            <div className="rw-classification__header">
                <span className="rw-classification__shield"><FontAwesomeIcon icon={faStar} /></span>
                <div>
                    <span className="rw-eyebrow">Your work</span>
                    <strong>Reward classification</strong>
                </div>
                {locked && <span className="rw-lock-label"><FontAwesomeIcon icon={faLock} /> Locked</span>}
            </div>

            {loading && <div className="rw-alert">Loading reward classification…</div>}
            {loadError && <div className="rw-alert is-error">{loadError}</div>}

            <label className={`rw-classification__option${difficult ? " is-checked" : ""}`}>
                <input type="checkbox" checked={difficult} disabled={locked || state.saving || loading} onChange={(event) => setDifficult(event.target.checked)} />
                <span className="rw-classification__option-icon"><FontAwesomeIcon icon={faStar} /></span>
                <span><strong>Difficult task</strong><small>+5 is sent to management review after completion</small></span>
                <b>+5</b>
            </label>

            <label className={`rw-classification__option${urgent ? " is-checked" : ""}`}>
                <input type="checkbox" checked={urgent} disabled={locked || state.saving || loading} onChange={(event) => setUrgent(event.target.checked)} />
                <span className="rw-classification__option-icon"><FontAwesomeIcon icon={faBolt} /></span>
                <span><strong>Urgent management request</strong><small>+10 is sent to management review after this work unit is completed</small></span>
                <b>+10</b>
            </label>

            <textarea
                className="rw-input rw-textarea"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why does this work qualify, or why is the classification being removed?"
                disabled={locked || state.saving || loading}
                maxLength={500}
            />
            {state.error && <div className="rw-alert is-error">{state.error}</div>}
            {state.success && <div className="rw-alert is-success">{state.success}</div>}
            <button className="rw-button is-primary" type="button" onClick={save} disabled={locked || state.saving || loading || !dirty || !reason.trim()}>
                {state.saving ? "Saving…" : "Save classification"}
            </button>
        </section>
    );
};

export default RewardClassificationPanel;
